import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTenant } from '../../context/TenantContext';
import {
  useCompaniesInRidesQuery,
  useCreateRunMutation,
  useFleetsInRoutesQuery,
  useRunProfilesQuery
} from '../../services/routing/queries';
import { namedCompany, namedFleet, namesById, parseIdList } from '../../utils/format';
import type { CompanyInRides, FleetInRoutes } from '../../services/routing/api';
import type { CreateRunBody } from '../../types/routing';

const fleetLabel = (fleet: FleetInRoutes) =>
  `${fleet.name} (${fleet.id})${fleet.routes == null ? '' : ` · ${fleet.routes} routes`}`;

const companyLabel = (company: CompanyInRides) =>
  `${company.name} (${company.id})${company.rides == null ? '' : ` · ${company.rides} rides`}`;

// A company with no rides that day cannot scope anything, so it is shown but not pickable.
const hasRides = (company: CompanyInRides) => (company.rides ?? 0) > 0;

// A fleet the day has no routes for cannot scope anything, so it is shown but not pickable.
const hasRoutes = (fleet: FleetInRoutes) => (fleet.routes ?? 0) > 0;

/** A profile plans itself: this screen only says which one, and which day. */
const NO_PROFILE = '';

export const NewRunPage = () => {
  const [fromDate, setFromDate] = useState('');
  const { tenant } = useTenant();
  const [profileId, setProfileId] = useState<string>(NO_PROFILE);
  const [groupA, setGroupA] = useState<string[]>([]);
  const [groupB, setGroupB] = useState<string[]>([]);
  const [companies, setCompanies] = useState<string[]>([]);
  const [routeIds, setRouteIds] = useState('');
  const [adminId, setAdminId] = useState('');

  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const createRun = useCreateRunMutation();

  const profileData = useRunProfilesQuery(tenant).data;
  const profiles = profileData?.profiles ?? [];
  const loadedProfile = profiles.find(profile => String(profile.profile_id) === profileId);
  // A profile is the selection, and the API refuses a run that sends both, so the pickers are
  // shut off rather than left looking as though they still applied.
  const byProfile = profileId !== NO_PROFILE;

  const { data: fleetData, isLoading: fleetsLoading } = useFleetsInRoutesQuery({
    fromDatetime: fromDate,
    tenant
  });
  const fleets = fleetData?.fleets ?? [];
  const { data: companyData, isLoading: companiesLoading } = useCompaniesInRidesQuery({
    fromDatetime: fromDate,
    tenant
  });
  const companyOptions = companyData?.companies ?? [];
  // The day's fleets and companies double as the loaded profile's names. Before a day is picked
  // there are none, and the recap falls back to labelled ids rather than reading blank.
  const fleetNames = namesById(fleets);
  const companyNames = namesById(companyOptions);
  const namedFleets = (ids: number[]) => ids.map(id => namedFleet(id, fleetNames)).join(', ');
  const companyLabelById = new Map(
    companyOptions.map(option => [String(option.id), companyLabel(option)])
  );
  const renderCompanies = (selected: string[]) =>
    selected.map(id => companyLabelById.get(id) ?? id).join(', ');

  // Same trap as the fleets: change the day and a company already picked may have no rides on
  // the new one, which would scope the run to nothing.
  const pickableCompanies = (selected: string[]) =>
    selected.filter(id =>
      companyOptions.some(option => String(option.id) === id && hasRides(option))
    );
  const onCompaniesChange = (selected: string[]) =>
    setCompanies(pickableCompanies(selected));

  const companyHint = !fromDate
    ? 'pick the planned day first'
    : companiesLoading
      ? 'loading the day\u2019s companies\u2026'
      : companyOptions.length
        ? `${companyOptions.filter(hasRides).length} of ${companyOptions.length} company(ies) `
          + 'have rides this day'
        : `no rides saved for \u201c${tenant}\u201d on this day`;
  const labelById = new Map(fleets.map(fleet => [String(fleet.id), fleetLabel(fleet)]));
  const inGroupA = (fleet: FleetInRoutes) => groupA.includes(String(fleet.id));
  const inGroupB = (fleet: FleetInRoutes) => groupB.includes(String(fleet.id));
  const remaining = fleets.filter(fleet => !inGroupA(fleet) && !inGroupB(fleet));

  const pickable = (selected: string[]) =>
    selected.filter(id => fleets.some(fleet => String(fleet.id) === id && hasRoutes(fleet)));

  // Greying an option only stops the pointer. Change the planned day and a fleet already picked
  // may have no routes on the new one, so drop those rather than silently scoping the run to a
  // fleet that cannot contribute anything. Only once a list has arrived — not mid-fetch.
  useEffect(() => {
    if (!fleets.length) return;
    setGroupA(previous => pickable(previous));
    setGroupB(previous => pickable(previous));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fleetData]);

  useEffect(() => {
    if (!companyOptions.length) return;
    setCompanies(previous => pickableCompanies(previous));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyData]);

  // A fleet taken into group A leaves group B — the groups never overlap.
  const onGroupAChange = (selected: string[]) => {
    const next = pickable(selected);
    setGroupA(next);
    setGroupB(previous => previous.filter(id => !next.includes(id)));
  };

  const renderFleets = (selected: string[]) =>
    selected.map(id => labelById.get(id) ?? id).join(', ');

  // The list is derived from the routes a run would plan, so "empty" nearly always means "no
  // routes", not "no fleets" — say which, or the fleet names look like they went missing.
  const emptyFleetHint =
    `no routes saved for \u201c${tenant}\u201d on this day \u2014 fleets come from routes, not the fleet list`;
  const routedCount = fleets.filter(hasRoutes).length;
  const fleetHint = !fromDate
    ? 'pick the planned day first'
    : fleetsLoading
      ? 'loading the day\u2019s fleets\u2026'
      : fleets.length
        ? `${routedCount} of ${fleets.length} fleet(s) have routes this day`
        : emptyFleetHint;

  const onSubmit = () => {
    if (!fromDate) {
      enqueueSnackbar('Pick the day to plan', { variant: 'error' });
      return;
    }
    const body: CreateRunBody = { from_datetime: fromDate, tenant };
    const routesOnly = parseIdList(routeIds);
    if (byProfile) {
      // route_ids is the one field a profile leaves to the caller: it picks particular routes out
      // of the selection rather than replacing it.
      body.profile_id = Number(profileId);
      if (routesOnly.length) body.route_ids = routesOnly;
      if (adminId) body.requested_by_admin_id = Number(adminId);
      startRun(body);
      return;
    }
    const selectedFleetIds = [...groupA, ...groupB].map(Number);

    // Group A plans before group B sees a ride — sent as an order, not just as a scope, so the
    // two boxes on this screen mean what they look like they mean.
    const orderedGroups = [groupA, groupB]
      .filter(group => group.length)
      .map(group => group.map(Number));

    if (selectedFleetIds.length) body.fleet_ids = selectedFleetIds;
    if (orderedGroups.length > 1) body.fleet_groups = orderedGroups;
    if (companies.length) body.company_ids = companies.map(Number);
    if (routesOnly.length) body.route_ids = routesOnly;
    if (adminId) body.requested_by_admin_id = Number(adminId);
    startRun(body);
  };

  const startRun = (body: CreateRunBody) =>
    createRun.mutate(body, {
      onSuccess: result => {
        enqueueSnackbar(`Run #${result.run_id} started`);
        navigate(`/runs/${result.run_id}`);
      },
      onError: error => enqueueSnackbar((error as Error).message, { variant: 'error' })
    });

  return (
    <Paper sx={{ p: 2, maxWidth: 760 }}>
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
        START A RUN
      </Typography>
      <Stack spacing={2}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} flexWrap="wrap" useFlexGap>
          <TextField
            label="Planned day (America/New_York)"
            type="date"
            required
            value={fromDate}
            onChange={event => setFromDate(event.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Tenant"
            value={tenant}
            helperText="picked in the top bar — the run plans this tenant's saved world"
            InputProps={{ readOnly: true }}
            sx={{ minWidth: { xs: 0, sm: 320 } }}
          />
        </Stack>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} flexWrap="wrap" useFlexGap alignItems={{ xs: 'stretch', sm: 'flex-start' }}>
          <TextField
            select
            label="Run profile"
            value={profileId}
            onChange={event => setProfileId(event.target.value)}
            helperText={
              profiles.length
                ? 'a saved selection — the fleets, their order and the companies'
                : `no profiles saved for \u201c${tenant}\u201d — pick the fleets below instead`
            }
            SelectProps={{ SelectDisplayProps: { 'aria-label': 'run profile' } }}
            sx={{ minWidth: { xs: 0, sm: 260 } }}
          >
            <MenuItem value={NO_PROFILE}>None — pick fleets below</MenuItem>
            {profiles.map(profile => (
              <MenuItem key={profile.profile_id} value={String(profile.profile_id)}>
                {profile.name}
              </MenuItem>
            ))}
          </TextField>
          {loadedProfile && (
            <Typography variant="body2" color="text.secondary" sx={{ pt: 1, maxWidth: 420 }}>
              Plans{' '}
              {loadedProfile.fleet_ids.length
                ? `fleets ${namedFleets(loadedProfile.fleet_ids)}`
                : 'every fleet'}
              {loadedProfile.fleet_groups.length
                ? `, in the order ${loadedProfile.fleet_groups
                    .map(namedFleets)
                    .join(' \u2192 ')}`
                : ''}
              {loadedProfile.company_ids.length
                ? `, for companies ${loadedProfile.company_ids
                    .map(id => namedCompany(id, companyNames))
                    .join(', ')}`
                : ", for every company's rides"}
              .
            </Typography>
          )}
        </Stack>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} flexWrap="wrap" useFlexGap>
          <TextField
            select
            label="Group A fleets"
            value={groupA}
            onChange={event => onGroupAChange(event.target.value as unknown as string[])}
            disabled={byProfile || !fleets.length}
            helperText={fleetHint}
            SelectProps={{
              multiple: true,
              renderValue: selected => renderFleets(selected as string[]),
              SelectDisplayProps: { 'aria-label': 'group A fleets' }
            }}
            sx={{ minWidth: { xs: 0, sm: 240 } }}
          >
            {fleets.map(fleet => (
              <MenuItem key={fleet.id} value={String(fleet.id)} disabled={!hasRoutes(fleet)}>
                {fleetLabel(fleet)}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Group B fleets"
            value={groupB}
            onChange={event => setGroupB(pickable(event.target.value as unknown as string[]))}
            disabled={byProfile || !fleets.length}
            helperText="group A's picks are removed from here; 0-route fleets are not pickable"
            SelectProps={{
              multiple: true,
              renderValue: selected => renderFleets(selected as string[]),
              SelectDisplayProps: { 'aria-label': 'group B fleets' }
            }}
            sx={{ minWidth: { xs: 0, sm: 240 } }}
          >
            {fleets.filter(fleet => !inGroupA(fleet)).map(fleet => (
              <MenuItem key={fleet.id} value={String(fleet.id)} disabled={!hasRoutes(fleet)}>
                {fleetLabel(fleet)}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Remaining fleets"
            value={remaining.map(fleet => String(fleet.id))}
            helperText="not in either group — read only"
            SelectProps={{
              multiple: true,
              readOnly: true,
              renderValue: selected => renderFleets(selected as string[]),
              SelectDisplayProps: { 'aria-label': 'remaining fleets' }
            }}
            sx={{ minWidth: { xs: 0, sm: 240 } }}
          >
            {remaining.map(fleet => (
              <MenuItem key={fleet.id} value={String(fleet.id)}>
                {fleetLabel(fleet)}
              </MenuItem>
            ))}
          </TextField>
        </Stack>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} flexWrap="wrap" useFlexGap>
          <TextField
            select
            label="Companies"
            value={companies}
            onChange={event => onCompaniesChange(event.target.value as unknown as string[])}
            disabled={byProfile || !companyOptions.length}
            helperText={companyHint}
            SelectProps={{
              multiple: true,
              renderValue: selected => renderCompanies(selected as string[]),
              SelectDisplayProps: { 'aria-label': 'companies' }
            }}
            sx={{ minWidth: { xs: 0, sm: 260 } }}
          >
            {companyOptions.map(option => (
              <MenuItem
                key={option.id}
                value={String(option.id)}
                disabled={!hasRides(option)}
              >
                {companyLabel(option)}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Route ids"
            placeholder="e.g. 601, 602"
            value={routeIds}
            onChange={event => setRouteIds(event.target.value)}
          />
          <TextField
            label="Requested by admin id"
            type="number"
            value={adminId}
            onChange={event => setAdminId(event.target.value)}
            sx={{ width: { xs: '100%', sm: 180 } }}
          />
        </Stack>

        <Stack direction="row" spacing={2}>
          <Button variant="contained" onClick={onSubmit} disabled={createRun.isPending}>
            Start run
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );
};

export default NewRunPage;
